"use client"

import { FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { ExternalLink, Loader2, PhoneOutgoing } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useStartOutgoingVoiceCallMutation } from "@/hooks/mutations/outgoing-voice-calls"
import { useOutgoingVoiceCallPhoneNumbersQuery } from "@/hooks/queries/outgoing-voice-calls"
import type { VoiceAgent } from "@/hooks/agents/voice"
import { OutgoingCallNumberSelect } from "@/components/agents/voice/outgoing-call-number-select"

type OutgoingCallDialogProps = {
  agent: VoiceAgent | null
  agents?: VoiceAgent[]
  isLoadingAgents?: boolean
  agentsError?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type OutgoingCallField = "voiceAgentId" | "fromNumber" | "toNumber" | "customInstructions"
type OutgoingCallFieldErrors = Partial<Record<OutgoingCallField, string>>

const LAST_OUTGOING_CALL_FROM_NUMBER_KEY = "versafy:last-outgoing-call-from-number"
const EMPTY_PHONE_NUMBERS: NonNullable<ReturnType<typeof useOutgoingVoiceCallPhoneNumbersQuery>["data"]> = []
const EMPTY_AGENTS: VoiceAgent[] = []

export function OutgoingCallDialog({
  agent,
  agents = EMPTY_AGENTS,
  isLoadingAgents = false,
  agentsError = null,
  open,
  onOpenChange,
}: OutgoingCallDialogProps) {
  const shouldSelectAgent = !agent
  const [selectedAgentId, setSelectedAgentId] = useState("")
  const [fromNumber, setFromNumber] = useState("")
  const [toNumber, setToNumber] = useState("")
  const [customInstructions, setCustomInstructions] = useState("")
  const [personalizeOpeningMessage, setPersonalizeOpeningMessage] = useState(true)
  const [validationErrors, setValidationErrors] = useState<OutgoingCallFieldErrors>({})
  const { data: phoneNumbers = EMPTY_PHONE_NUMBERS, isLoading, error } = useOutgoingVoiceCallPhoneNumbersQuery(open)
  const startCallMutation = useStartOutgoingVoiceCallMutation()
  const resetStartCallMutationRef = useRef(startCallMutation.reset)

  const availableNumbers = useMemo(
    () => phoneNumbers.filter(number => number.phoneNumber && !number.lendingSourceTenantId),
    [phoneNumbers],
  )
  const selectableAgents = useMemo(
    () => agents.filter(candidate => candidate.status === "active"),
    [agents],
  )
  const selectedAgent = useMemo(
    () => agent ?? selectableAgents.find(candidate => candidate.id === selectedAgentId) ?? null,
    [agent, selectableAgents, selectedAgentId],
  )

  useEffect(() => {
    if (!open) {
      setSelectedAgentId("")
      setFromNumber("")
      setToNumber("")
      setCustomInstructions("")
      setPersonalizeOpeningMessage(true)
      setValidationErrors({})
      resetStartCallMutationRef.current()
    }
  }, [open])

  useEffect(() => {
    resetStartCallMutationRef.current = startCallMutation.reset
  }, [startCallMutation.reset])

  useEffect(() => {
    if (!open || fromNumber || availableNumbers.length === 0) return

    const lastUsedFromNumber = window.localStorage.getItem(LAST_OUTGOING_CALL_FROM_NUMBER_KEY)
    const savedNumberIsAvailable = availableNumbers.some(number => number.phoneNumber === lastUsedFromNumber)
    setFromNumber(savedNumberIsAvailable && lastUsedFromNumber ? lastUsedFromNumber : availableNumbers[0].phoneNumber)
  }, [availableNumbers, fromNumber, open])

  useEffect(() => {
    if (!open || !fromNumber || availableNumbers.length === 0) return

    const selectedNumberIsAvailable = availableNumbers.some(number => number.phoneNumber === fromNumber)
    if (!selectedNumberIsAvailable) {
      setFromNumber(availableNumbers[0].phoneNumber)
    }
  }, [availableNumbers, fromNumber, open])

  useEffect(() => {
    if (!open || !shouldSelectAgent || selectedAgentId || selectableAgents.length === 0) return

    setSelectedAgentId(selectableAgents[0].id)
  }, [open, selectableAgents, selectedAgentId, shouldSelectAgent])

  useEffect(() => {
    if (!open || !shouldSelectAgent || !selectedAgentId || selectableAgents.length === 0) return

    const selectedAgentIsAvailable = selectableAgents.some(candidate => candidate.id === selectedAgentId)
    if (!selectedAgentIsAvailable) {
      setSelectedAgentId(selectableAgents[0].id)
    }
  }, [open, selectableAgents, selectedAgentId, shouldSelectAgent])

  const isSubmitting = startCallMutation.isPending
  const canSubmit = Boolean(selectedAgent?.id && fromNumber && toNumber && !isSubmitting)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedAgent || !canSubmit) return

    try {
      const trimmedCustomInstructions = customInstructions.trim()
      await startCallMutation.mutateAsync({
        voiceAgentId: selectedAgent.id,
        fromNumber,
        toNumber,
        customInstructions: trimmedCustomInstructions || undefined,
        personalizeOpeningMessage,
        setErrors: errors => setValidationErrors(getOutgoingCallFieldErrors(errors)),
      })
      window.localStorage.setItem(LAST_OUTGOING_CALL_FROM_NUMBER_KEY, fromNumber)

      onOpenChange(false)
    } catch {
      // withQueryHandling already renders the error toast. Keep the dialog
      // open and avoid surfacing handled mutation failures to the dev overlay.
    }
  }

  function clearValidationError(field: OutgoingCallField) {
    setValidationErrors(current => {
      if (!current[field]) return current

      const next = { ...current }
      delete next[field]
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Make a call</DialogTitle>
          <DialogDescription>
            <span>Start an outgoing call from</span> {selectedAgent?.name ?? "a selected voice agent"}<span>. Only phone numbers
            with outbound calling enabled on the Phone Numbers page appear here.</span>
            {" "}
            <br/>
            <br/>
            <span>You can also start calls through the</span>{" "}
            <a
              href="/api/tenant-api/docs"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              <span>Tenant API</span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <span>.</span>
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {agentsError && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {agentsError}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {error instanceof Error ? error.message : "Failed to load phone numbers."}
              </AlertDescription>
            </Alert>
          )}

          {shouldSelectAgent && (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="outgoing-voice-agent">
                Voice agent
              </label>
              <Select
                value={selectedAgentId}
                onValueChange={(value) => {
                  setSelectedAgentId(value)
                  clearValidationError("voiceAgentId")
                }}
                disabled={isLoadingAgents || isSubmitting}
              >
                <SelectTrigger
                  id="outgoing-voice-agent"
                  className={validationErrors.voiceAgentId ? "border-destructive focus:ring-destructive" : undefined}
                  aria-invalid={Boolean(validationErrors.voiceAgentId)}
                  aria-describedby={validationErrors.voiceAgentId ? "outgoing-voice-agent-error" : undefined}
                >
                  <SelectValue placeholder={isLoadingAgents ? "Loading voice agents..." : "Select a voice agent"} />
                </SelectTrigger>
                <SelectContent>
                  {selectableAgents.map(candidate => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.voiceAgentId ? (
                <p id="outgoing-voice-agent-error" role="alert" className="text-xs font-medium text-destructive">
                  {validationErrors.voiceAgentId}
                </p>
              ) : null}
              {!isLoadingAgents && selectableAgents.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No active voice agents are available. Activate a voice agent before starting an outgoing call.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="outgoing-from-number">
              Call from
            </label>
            <OutgoingCallNumberSelect
              numbers={phoneNumbers}
              value={fromNumber}
              loading={isLoading}
              disabled={isLoading || isSubmitting}
              invalid={Boolean(validationErrors.fromNumber)}
              describedBy={validationErrors.fromNumber ? "outgoing-from-number-error" : undefined}
              onValueChange={(value) => {
                setFromNumber(value)
                clearValidationError("fromNumber")
              }}
            />
            {validationErrors.fromNumber ? (
              <p id="outgoing-from-number-error" role="alert" className="text-xs font-medium text-destructive">
                {validationErrors.fromNumber}
              </p>
            ) : null}
            {!isLoading && phoneNumbers.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No phone numbers are enabled for outbound calling yet. Go to Phone Numbers → Numbers and enable or import a voice-capable number.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="outgoing-to-number">
              Recipient number
            </label>
            <Input
              id="outgoing-to-number"
              value={toNumber}
              onChange={(event) => {
                setToNumber(event.target.value)
                clearValidationError("toNumber")
              }}
              placeholder="+15551234567"
              autoComplete="tel"
              autoCorrect="off"
              spellCheck={false}
              disabled={isSubmitting}
              className={validationErrors.toNumber ? "border-destructive focus-visible:ring-destructive" : undefined}
              aria-invalid={Boolean(validationErrors.toNumber)}
              aria-describedby={validationErrors.toNumber
                ? "outgoing-to-number-error"
                : "outgoing-to-number-description"}
            />
            <p id="outgoing-to-number-description" className="text-xs text-muted-foreground">
              Use E.164 format, including the country code.
            </p>
            {validationErrors.toNumber ? (
              <p id="outgoing-to-number-error" role="alert" className="text-xs font-medium text-destructive">
                {validationErrors.toNumber}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="outgoing-custom-instructions">
              Custom instructions
            </label>
            <Textarea
              id="outgoing-custom-instructions"
              value={customInstructions}
              onChange={(event) => {
                setCustomInstructions(event.target.value)
                clearValidationError("customInstructions")
              }}
              placeholder="Optional context or instructions for this specific call."
              maxLength={2000}
              disabled={isSubmitting}
              className={validationErrors.customInstructions
                ? "border-destructive focus-visible:ring-destructive"
                : undefined}
              aria-invalid={Boolean(validationErrors.customInstructions)}
              aria-describedby={validationErrors.customInstructions
                ? "outgoing-custom-instructions-error"
                : "outgoing-custom-instructions-description"}
            />
            <p id="outgoing-custom-instructions-description" className="text-xs text-muted-foreground">
              Optional. Extra context for this call only. Does not change the agent's saved prompt.
            </p>
            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1 pt-1">
              <Checkbox
                id="outgoing-personalize-opening-message"
                checked={personalizeOpeningMessage}
                onCheckedChange={checked => setPersonalizeOpeningMessage(checked === true)}
                disabled={isSubmitting}
                aria-describedby="outgoing-opening-message-description"
              />
              <label className="min-w-0 cursor-pointer text-sm font-medium" htmlFor="outgoing-personalize-opening-message">
                Personalize opening message
              </label>
              <p id="outgoing-opening-message-description" className="col-start-2 min-w-0 text-xs text-muted-foreground">
                Use these instructions to tailor the greeting for this call. Uncheck to keep the
                agent's opening message unchanged and use instructions as context only, like Test.
              </p>
            </div>
            {validationErrors.customInstructions ? (
              <p
                id="outgoing-custom-instructions-error"
                role="alert"
                className="text-xs font-medium text-destructive"
              >
                {validationErrors.customInstructions}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PhoneOutgoing className="w-4 h-4 mr-2" />
                  Start call
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function getOutgoingCallFieldErrors(errors: Record<string, unknown>): OutgoingCallFieldErrors {
  return {
    voiceAgentId: getStringError(errors.voiceAgentId),
    fromNumber: getStringError(errors.fromNumber),
    toNumber: getStringError(errors.toNumber),
    customInstructions: getStringError(errors.customInstructions),
  }
}

function getStringError(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined
}
